import { useCallback, useState } from 'react';
import {
    OptionStyles,
    ProductOption,
    ProductOptionChangedEvent,
    ProductOptionModel,
    ProductOptionsTranslations,
} from '@vp/product-options-ui';
import { Box, Button, FlexBox, Icon, Spinner, Typography, getTokenCssVar } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { usePrintProductConfiguratorQuery } from '../../../graphql/product-options.generated';
import { WizardOptionList, WizardStep } from '../../WizardStep';
import { PRINT_OPTIONS_CHOICE_CUSTOMER, PRINT_OPTIONS_CHOICE_DESIGNER } from './types';
import type { PrintProductRef } from '../../../product/types';
import type { WizardStepDefinition } from '../../WizardStep';
import type { PrintOptionsQuestion } from './parseOptions';
import type { PrintOptionsReply } from './parseReply';
import type { PrintOption, PrintOptionsChoice } from './types';

export type PrintOptionsSelectorViewProps = {
    question: PrintOptionsQuestion;
    printProduct: PrintProductRef | undefined;
    onSubmit: (reply: PrintOptionsReply) => void;
    /**
     * Asks the assistant to explain the choices. Required for the same reason as on the
     * style-tiles selector: the footer lays help and Continue out with `space-between`, so an
     * absent help button would silently pull Continue to the left edge rather than degrade.
     */
    onRequestHelp: (options: ReadonlyArray<{ name: string }>) => void;
};

/**
 * Above this many values an option renders as a dropdown rather than a row of buttons. Ported
 * from the pilot, along with the `Size` special case below.
 */
const MAX_VALUES_FOR_BUTTONS = 4;

/**
 * `Size` is always a dropdown regardless of how few values it has — it is the option customers
 * scan for by name, and the pilot ships it this way.
 */
function getOptionStyle(key: string, valuesCount: number): OptionStyles {
    if (key === 'Size') return 'dropdown';
    return valuesCount > MAX_VALUES_FOR_BUTTONS ? 'dropdown' : 'buttons';
}

const SELECTOR_SCROLL_MAX_HEIGHT = '270px';

/**
 * The print-configuration composer selector: a two-step wizard over
 * `printProductConfigurator`.
 *
 * Step 1 asks whether the customer wants to specify the product themselves; step 2, reached only
 * if they do, shows the configurable options. Step 2 is the reason this selector queries the
 * graph rather than reading a payload — see the exception documented on `GenerativeUiDescriptor`.
 *
 * Renders only the card. Where it sits — the composer's background band and centred column —
 * belongs to `ChatInterface`, exactly as it does for `ChatInput` and the style-tiles selector.
 */
export function PrintOptionsSelectorView({
    question,
    printProduct,
    onSubmit,
    onRequestHelp,
}: PrintOptionsSelectorViewProps) {
    const hasPreselectedSelections =
        !!question.preselectedSelections && Object.keys(question.preselectedSelections).length > 0;

    // Preselected values imply the customer already chose to specify the product, so they
    // override an absent or contradictory `preselectedChoice` rather than being stranded on a
    // step the wizard would never reach.
    const startAtStep2 =
        question.preselectedChoice === PRINT_OPTIONS_CHOICE_CUSTOMER || hasPreselectedSelections;

    const [activeStep, setActiveStep] = useState(startAtStep2 ? 2 : 1);
    const [choice, setChoice] = useState<PrintOptionsChoice | ''>(
        hasPreselectedSelections ? PRINT_OPTIONS_CHOICE_CUSTOMER : question.preselectedChoice ?? ''
    );
    const [selections, setSelections] = useState<Record<string, string>>(
        question.preselectedSelections ?? {}
    );
    const [helpRequested, setHelpRequested] = useState(false);

    const selectedAttributes = Object.entries(selections).map(([key, value]) => ({
        attributeKey: key,
        valueKey: value,
    }));

    const {
        data: configData,
        previousData,
        loading,
        error,
    } = usePrintProductConfiguratorQuery({
        variables: {
            input: {
                mpvId: printProduct?.mpvId ?? '',
                tenant: printProduct?.tenant ?? '',
                selectedAttributes,
            },
        },
        // No product to configure, or nothing to configure yet: step 1 is answerable without
        // options, so asking for them before the customer opts in wastes a round trip.
        skip: activeStep !== 2 || !printProduct,
        fetchPolicy: 'cache-and-network',
    });

    // Falling back to `previousData` keeps the current options mounted while the next
    // compatibility query is in flight. Without it every selection would blank the list and
    // rebuild it, which reads as a flicker on what is meant to be a direct manipulation.
    const productOptions: PrintOption[] = (
        (configData ?? previousData)?.printProductConfigurator?.productOptions ?? []
    ).map((option) => ({
        key: option.key,
        name: option.name,
        values: option.values.map((value) => ({
            key: value.key,
            name: value.name,
            isCompatible: value.isCompatible,
            ...(value.imageUrl ? { imageUrl: value.imageUrl } : {}),
        })),
    }));

    const hasOptions = productOptions.length > 0;
    const failedToLoad = (!!error || !printProduct) && !hasOptions;

    const handleOptionSelect = useCallback((optionKey: string, valueKey: string) => {
        setSelections((previous) => ({ ...previous, [optionKey]: valueKey }));
    }, []);

    const submitDesigner = () => {
        onSubmit(buildReply({ choice: PRINT_OPTIONS_CHOICE_DESIGNER }));
    };

    const submitCustomer = () => {
        const selectionDetails = productOptions
            .filter((option) => selections[option.key])
            .map((option) => {
                const selectedValue = option.values.find(
                    (value) => value.key === selections[option.key]
                );
                return {
                    optionName: option.name,
                    valueName: selectedValue?.name ?? selections[option.key],
                    ...(selectedValue?.imageUrl ? { imageUrl: selectedValue.imageUrl } : {}),
                };
            });

        onSubmit(
            buildReply({
                choice: PRINT_OPTIONS_CHOICE_CUSTOMER,
                ...(Object.keys(selections).length > 0 ? { selections } : {}),
                ...(selectionDetails.length > 0 ? { selectionDetails } : {}),
            })
        );
    };

    // One help request per selector: the answer arrives as the next assistant turn, and a second
    // identical request before then would just duplicate the message in the thread.
    const handleRequestHelp = () => {
        setHelpRequested(true);
        // Before options load there is nothing to describe but the step-1 choice itself, so send
        // those two labels instead. Incompatible values are filtered out — the assistant should
        // not talk about a finish the customer cannot pick.
        onRequestHelp(
            hasOptions
                ? productOptions.map((option) => ({ name: option.name }))
                : [{ name: CHOICE_LABEL_CUSTOMER() }, { name: CHOICE_LABEL_DESIGNER() }]
        );
    };

    const helpButton = (
        <Button
            skin="tertiary"
            iconPosition="left"
            onClick={handleRequestHelp}
            disabled={helpRequested}
        >
            <Icon iconType="aiSupport" />
            {__('Help me answer')}
        </Button>
    );

    const stepOne: WizardStepDefinition = {
        title: question.title?.trim()
            ? question.title
            : __('Would you like to select your product specifications?'),
        content: (
            <Box
                style={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    maxHeight: SELECTOR_SCROLL_MAX_HEIGHT,
                    padding: getTokenCssVar('SwanSemSpace1'),
                }}
            >
                <WizardOptionList
                    aria-label={__('Would you like to select your product specifications?')}
                    options={[
                        {
                            value: PRINT_OPTIONS_CHOICE_CUSTOMER,
                            icon: <Icon iconType="printedProducts" size="standard" />,
                            label: CHOICE_LABEL_CUSTOMER(),
                        },
                        {
                            value: PRINT_OPTIONS_CHOICE_DESIGNER,
                            icon: <Icon iconType="workWithAPro" size="standard" />,
                            label: CHOICE_LABEL_DESIGNER(),
                        },
                    ]}
                    selectedValue={choice}
                    onSelectedValueChange={(value) => setChoice(value as PrintOptionsChoice)}
                />
            </Box>
        ),
        footer: (
            <FlexBox justifyContent="space-between" alignItems="center" gap={3}>
                {helpButton}
                <Button
                    skin="secondary"
                    onClick={() => {
                        if (!choice) return;
                        if (choice === PRINT_OPTIONS_CHOICE_DESIGNER) {
                            submitDesigner();
                        } else {
                            setActiveStep(2);
                        }
                    }}
                    disabled={!choice}
                >
                    {choice === PRINT_OPTIONS_CHOICE_CUSTOMER ? __('Next') : __('Continue')}
                </Button>
            </FlexBox>
        ),
        // The chevron must not skip the choice: step 2 configures a product the customer has not
        // yet said they want to configure.
        nextDisabled: choice !== PRINT_OPTIONS_CHOICE_CUSTOMER,
    };

    const stepTwo: WizardStepDefinition = {
        title: __('Select your product options'),
        // Defers heading focus until options land, so the screen reader announces a populated
        // step rather than an empty one.
        isReady: !loading,
        content: (
            <Box
                style={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    maxHeight: SELECTOR_SCROLL_MAX_HEIGHT,
                    padding: getTokenCssVar('SwanSemSpace1'),
                }}
            >
                <ProductOptionsTranslations locale="en-US">
                    <Box style={{ position: 'relative' }}>
                        {loading && (
                            <Box
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1000,
                                }}
                            >
                                <Spinner
                                    accessibleText={__('Loading product options')}
                                    size="super"
                                />
                            </Box>
                        )}
                        {/* Dimmed rather than unmounted, so the options the customer is working
                            through do not jump out from under the cursor on every refetch. */}
                        <Box
                            style={{
                                opacity: loading ? 0.5 : 1,
                                pointerEvents: loading ? 'none' : 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: getTokenCssVar('SwanSemSpace5'),
                            }}
                        >
                            {failedToLoad && (
                                <Typography fontSkin="body-standard" textColor="error">
                                    {__(
                                        'Failed to load product options. You can continue without selecting.'
                                    )}
                                </Typography>
                            )}
                            {productOptions.map((option) => {
                                // Custom sizing is not offered in chat: it needs a dimension
                                // form the pilot never built here, and an inert "Custom" choice
                                // that silently does nothing is worse than its absence.
                                const values = option.values.filter(
                                    (value) => value.key !== 'Custom'
                                );
                                const style = getOptionStyle(option.key, values.length);

                                return (
                                    <Box
                                        key={option.key}
                                        pb={style === 'buttons' ? 0 : 5}
                                        data-testid={`product-option-${option.key.toLowerCase()}`}
                                    >
                                        <ProductOption
                                            productOption={toProductOptionModel({
                                                option: { ...option, values },
                                                selectedKey: selections[option.key],
                                                style,
                                            })}
                                            tenant={printProduct?.tenant ?? ''}
                                            currency=""
                                            fractionDigits={2}
                                            locale="en-US"
                                            priceDisplayMode="none"
                                            showIncompatibleChoices={true}
                                            skin="standard"
                                            onChanged={(event: ProductOptionChangedEvent) => {
                                                handleOptionSelect(
                                                    option.key,
                                                    event.choiceKey || ''
                                                );
                                            }}
                                            uiDensityUpdate={true}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </ProductOptionsTranslations>
            </Box>
        ),
        footer: (
            <FlexBox justifyContent="space-between" alignItems="center" gap={3}>
                {helpButton}
                <Button skin="secondary" onClick={submitCustomer} disabled={loading}>
                    {__('Continue')}
                </Button>
            </FlexBox>
        ),
    };

    // One step until the customer opts into configuring the product, which is what makes
    // WizardStep hide its pager on step 1 and show "1 of 2" once they have.
    const steps = choice === PRINT_OPTIONS_CHOICE_CUSTOMER ? [stepOne, stepTwo] : [stepOne];

    return (
        <WizardStep
            steps={steps}
            activeStep={activeStep}
            onStepChange={setActiveStep}
            previousStepLabel={__('Previous step')}
            nextStepLabel={__('Next step')}
        />
    );
}

/*
 * Called rather than hoisted to a const: `__()` must run at render time to pick up the i18n
 * context, and these two labels are needed in three places each (the option list, the help
 * payload, and the answered view's counterpart).
 */
function CHOICE_LABEL_CUSTOMER() {
    return __('I know my product specs');
}

function CHOICE_LABEL_DESIGNER() {
    return __('Let my designer decide');
}

/**
 * Builds the reply, including the duplicated `value` half the backend maps onto brief fields.
 *
 * Field order matters: the wire content is a JSON string compared byte-for-byte against the
 * pilot's in `descriptor.test.tsx`, so `choice` precedes `value` here as it does there.
 */
function buildReply({
    choice,
    selections,
    selectionDetails,
}: Omit<PrintOptionsReply, 'value'>): PrintOptionsReply {
    return {
        choice,
        value: {
            productOptionsDecision: choice,
            ...(selections && Object.keys(selections).length > 0
                ? { productOptions: JSON.stringify(selections) }
                : {}),
        },
        ...(selections ? { selections } : {}),
        ...(selectionDetails ? { selectionDetails } : {}),
    };
}

function toProductOptionModel({
    option,
    selectedKey,
    style,
}: {
    option: PrintOption;
    selectedKey: string | undefined;
    style: OptionStyles;
}): ProductOptionModel {
    return {
        key: option.key,
        name: option.name,
        style,
        choices: option.values.map((value) => ({
            key: value.key,
            name: value.name,
            isCompatible: value.isCompatible,
            image: value.imageUrl || undefined,
        })),
        selectedKey,
    };
}
